"use client"

// React
import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ComponentType,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react"

// Libraries
import { SearchIcon } from "@icons/SearchIcon"

// UI primitives
import { Command, CommandInput, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { HIDE_NATIVE_SCROLLBAR, ScrollbarOverlay } from "@/components/ui/scrollbar-overlay"
// Hooks and utilities
import { cn } from "@/lib/utils"
import { useScrollToSelected } from "@/react/hooks/useScrollToSelected"
import { MIN_SEARCH_LENGTH } from "@/react/utils/searchQuery"

/** Distance from bottom (px) at which onReachEnd fires for infinite scroll */
const INFINITE_SCROLL_THRESHOLD_PX = 80

/** Height for search bar and footer rows */
export const DROPDOWN_ROW_HEIGHT_PX = "53px"
export const DROPDOWN_ROW_HEIGHT = "h-[53px]"

/** Hairline border width used across dropdown UI */
const HAIRLINE_BORDER = "border-[0.5px]"

/** Surface of the dropdown popover, minus its width */
export const DROPDOWN_CONTENT_CLASS = cn(
  "max-h-[var(--radix-popover-content-available-height)] overflow-hidden rounded-input border-dinnerplate-white p-0 bg-off-white font-inter shadow-soft",
  HAIRLINE_BORDER,
)
const DEFAULT_COLLISION_PADDING = 16
const MIN_AVAILABLE_LIST_HEIGHT = "96px"
const SEARCH_AND_FOOTER_HEIGHT = "106px"
// No row has this value, so cmdk opens with nothing highlighted instead of the first row.
const NO_HIGHLIGHTED_ROW = "\u0000no-highlighted-row"
const ResetHighlightContext = createContext<() => void>(() => {})

/* -------------------------------------------------------------------------- */
/*  SearchableDropdown – compound component with cmdk search + keyboard nav   */
/* -------------------------------------------------------------------------- */

export interface SearchableDropdownProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  children: ReactNode
  /** Pass false to disable cmdk's built-in filtering (for server-side search). Default: true */
  shouldFilter?: boolean
  selectedValue?: string
  searchPlaceholder?: string
  /** Value to pre-fill the search input with when the dropdown opens */
  defaultSearchValue?: string
  /** Controlled search input value. When provided the consumer owns the text
   *  (so it can reset it without closing); otherwise it's internal state. */
  searchValue?: string
  /** Callback fired when the search input value changes. Required when shouldFilter is false. */
  onSearchChange?: (value: string) => void
  /**
   * Minimum characters before a search fires. Below it, the input still shows
   * what's typed but `onSearchChange` receives "" (no fetch) and a hint is
   * shown. Defaults to MIN_SEARCH_LENGTH for server-side search
   * (`shouldFilter=false`) and off (0) for client-side filtering. Pass 0 to
   * opt a server-side dropdown out.
   */
  minSearchLength?: number
  /**
   * Force the magnifying glass icon on the right of the search input.
   * Default: only shown when `shouldFilter=false` (server-side search).
   */
  showSearchIcon?: boolean
  /** Popover side relative to the trigger. Default: radix auto ("bottom", flips on collision). */
  side?: "top" | "right" | "bottom" | "left"
  /** Pass false to keep the popover on `side` instead of flipping when it would
   * overflow the viewport. Default: true (radix default). */
  avoidCollisions?: boolean
  /** Padding from viewport or collision boundary before the popover flips or resizes. */
  collisionPadding?: ComponentPropsWithoutRef<typeof PopoverContent>["collisionPadding"]
  /** Popover width as a CSS length. Default: the trigger's width. */
  contentWidth?: string
  /** Extra classes for the popover, e.g. a phone-only min width. */
  contentClassName?: string
  /** Radix modal: blocks page scroll and outside clicks while open. Default: false. */
  modal?: boolean
}

function SearchableDropdownRoot({
  isOpen,
  onOpenChange,
  children,
  shouldFilter = true,
  selectedValue,
  searchPlaceholder = "Search...",
  defaultSearchValue = "",
  searchValue: controlledSearchValue,
  onSearchChange,
  minSearchLength,
  showSearchIcon: showSearchIconProp,
  side,
  avoidCollisions,
  collisionPadding = DEFAULT_COLLISION_PADDING,
  contentWidth,
  contentClassName,
  modal = false,
}: SearchableDropdownProps) {
  const [internalSearchValue, setInternalSearchValue] = useState("")
  const searchValue = controlledSearchValue ?? internalSearchValue
  const handleScrollToSelected = useScrollToSelected(selectedValue)
  const showSearchIcon = showSearchIconProp ?? !shouldFilter

  const restingHighlight = selectedValue || NO_HIGHLIGHTED_ROW
  const [highlightedValue, setHighlightedValue] = useState(restingHighlight)
  const [wasOpen, setWasOpen] = useState(isOpen)
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen)
    if (isOpen) setHighlightedValue(restingHighlight)
  }
  const resetHighlight = useCallback(() => setHighlightedValue(restingHighlight), [restingHighlight])

  // Gate the search at a minimum length: server-side dropdowns default to
  // MIN_SEARCH_LENGTH, client-side filtering stays instant (0). Below the
  // threshold the input still shows what's typed, but the emitted value is ""
  // so no fetch fires. Controlled consumers own their text, so they gate their
  // own fetch — we leave their emitted value untouched.
  const effectiveMinLength = minSearchLength ?? (shouldFilter ? 0 : MIN_SEARCH_LENGTH)
  const gateEmittedValue = useCallback(
    (value: string) =>
      effectiveMinLength > 0 &&
      controlledSearchValue === undefined &&
      value.trim().length < effectiveMinLength
        ? ""
        : value,
    [effectiveMinLength, controlledSearchValue],
  )
  const trimmedLength = searchValue.trim().length
  const showHint = effectiveMinLength > 0 && trimmedLength > 0 && trimmedLength < effectiveMinLength

  const handleSearchChange = useCallback(
    (value: string) => {
      setInternalSearchValue(value)
      onSearchChange?.(gateEmittedValue(value))
    },
    [onSearchChange, gateEmittedValue],
  )

  const handleOpenChange = useCallback(
    (nextIsOpen: boolean) => {
      onOpenChange(nextIsOpen)
      if (nextIsOpen && defaultSearchValue) {
        setInternalSearchValue(defaultSearchValue)
        onSearchChange?.(gateEmittedValue(defaultSearchValue))
      } else if (!nextIsOpen) {
        setInternalSearchValue("")
        onSearchChange?.("")
      }
    },
    [onOpenChange, onSearchChange, defaultSearchValue, gateEmittedValue],
  )

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
      <SlotRenderer slotType={SearchableDropdownTrigger}>{children}</SlotRenderer>

      <PopoverContent
        className={cn("flex w-[var(--radix-popover-trigger-width)] flex-col", DROPDOWN_CONTENT_CLASS, contentClassName)}
        align="start"
        side={side}
        avoidCollisions={avoidCollisions}
        collisionPadding={collisionPadding}
        style={contentWidth ? { width: contentWidth } : undefined}
        onOpenAutoFocus={handleScrollToSelected}
      >
        <Command
          shouldFilter={shouldFilter}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
          className="bg-transparent"
        >
          <SearchableDropdownSearchBar
            value={searchValue}
            onValueChange={handleSearchChange}
            placeholder={searchPlaceholder}
            showSearchIcon={showSearchIcon}
            hint={showHint ? `Type ${effectiveMinLength} characters to start search` : null}
          />

          <SlotRenderer slotType={SearchableDropdownTabs}>{children}</SlotRenderer>
          {/* Below the min length only the hint shows — the list (and its
              browse/empty state) is hidden so a sub-threshold term neither
              searches nor shows unfiltered results. At 0 chars the hint is off,
              so browse-on-open still renders the list. */}
          <ResetHighlightContext.Provider value={resetHighlight}>
            {!showHint && <SlotRenderer slotType={SearchableDropdownList}>{children}</SlotRenderer>}
          </ResetHighlightContext.Provider>
          <SlotRenderer slotType={SearchableDropdownFooter}>{children}</SlotRenderer>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/* -------------------------------------------------------------------------- */
/*  SearchableDropdownSearchBar – the search row, usable inside any Command    */
/* -------------------------------------------------------------------------- */

export interface SearchableDropdownSearchBarProps {
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  showSearchIcon?: boolean
  hint?: string | null
}

export function SearchableDropdownSearchBar({
  value,
  onValueChange,
  placeholder,
  showSearchIcon = false,
  hint = null,
}: SearchableDropdownSearchBarProps) {
  return (
    /* Grows to fit the below-threshold hint (input stays put). Drop the bottom
       border while the hint shows: the list is hidden, so the border would
       otherwise stack against the footer's separator into a double line. */
    <div className={cn("flex shrink-0 flex-col justify-center rounded-t-input px-6 bg-off-white", hint ? "py-3" : cn(DROPDOWN_ROW_HEIGHT, "border-b border-dinnerplate-white"))}>
      <div className="flex items-center">
        <CommandInput
          hideIcon
          wrapperClassName="flex-1 border-0 px-0 bg-transparent"
          className="h-auto !border-transparent !ring-0 !outline-none !shadow-none p-0 font-inter text-sm text-off-black placeholder:text-grey-light"
          placeholder={placeholder}
          value={value}
          onValueChange={onValueChange}
        />
        {showSearchIcon && <SearchIcon className="size-5 shrink-0 text-grey" />}
      </div>
      {hint && (
        <p className="mt-1.5 font-inter text-xs leading-tight text-grey" aria-live="polite">
          {hint}
        </p>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Slot helpers – extract compound children by type                          */
/* -------------------------------------------------------------------------- */

function SlotRenderer<T>({ slotType, children }: { slotType: ComponentType<T>; children: ReactNode }) {
  const childArray = Array.isArray(children) ? children : [children]
  const slots: ReactNode[] = []

  for (const child of childArray) {
    if (child && typeof child === "object" && "type" in child && child.type === slotType) {
      slots.push(child)
    }
  }

  return <>{slots}</>
}

/* -------------------------------------------------------------------------- */
/*  SearchableDropdown.Trigger                                                */
/* -------------------------------------------------------------------------- */

function SearchableDropdownTrigger({ children }: { children: ReactNode }) {
  return <PopoverTrigger asChild>{children}</PopoverTrigger>
}

/* -------------------------------------------------------------------------- */
/*  SearchableDropdown.Tabs — optional strip between search and list          */
/* -------------------------------------------------------------------------- */

function SearchableDropdownTabs({ children }: { children: ReactNode }) {
  return (
    <div className={cn("border-b bg-off-white", HAIRLINE_BORDER, "border-dinnerplate-white")}>
      {children}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  SearchableDropdown.List                                                   */
/* -------------------------------------------------------------------------- */

export interface SearchableDropdownListProps {
  children: ReactNode
  showFadeGradient?: boolean
  maxHeight?: string
  availableHeightOffset?: string
  className?: string
  onReachEnd?: () => void
  isLoadingMore?: boolean
}

function SearchableDropdownList({
  children,
  showFadeGradient = false,
  maxHeight = "320px",
  availableHeightOffset = SEARCH_AND_FOOTER_HEIGHT,
  className,
  onReachEnd,
  isLoadingMore = false,
}: SearchableDropdownListProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const resetHighlight = useContext(ResetHighlightContext)
  const [canScrollDown, setCanScrollDown] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)
  const onReachEndRef = useRef(onReachEnd)
  useEffect(() => { onReachEndRef.current = onReachEnd }, [onReachEnd])

  const updateScrollState = useCallback(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return
    const distanceToBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight
    setCanScrollDown(distanceToBottom > 1)
    setIsScrollable(scrollContainer.scrollHeight > scrollContainer.clientHeight + 1)

    if (distanceToBottom > 0 && distanceToBottom < INFINITE_SCROLL_THRESHOLD_PX && onReachEndRef.current && !isLoadingMore) {
      onReachEndRef.current()
    }
  }, [isLoadingMore])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    updateScrollState()
    const observer = new ResizeObserver(updateScrollState)
    observer.observe(scrollContainer)
    Array.from(scrollContainer.children).forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [updateScrollState])

  // When this dropdown lives inside a Radix Dialog, `react-remove-scroll`
  // intercepts wheel events on the Portal'd content and the scroll
  // container never receives them. We attach a non-passive native wheel
  // listener that scrolls manually and prevents the default — that lets
  // the dropdown scroll inside modals without affecting non-modal usage
  // (preventDefault replaces the would-be native scroll with an
  // equivalent manual scroll).
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const onWheel = (event: WheelEvent) => {
      const canScrollVertically = scrollContainer.scrollHeight > scrollContainer.clientHeight
      if (!canScrollVertically) return
      event.preventDefault()
      event.stopPropagation()
      scrollContainer.scrollTop += event.deltaY
    }

    scrollContainer.addEventListener("wheel", onWheel, { passive: false })
    return () => scrollContainer.removeEventListener("wheel", onWheel)
  }, [])

  const availableHeight = `max(${MIN_AVAILABLE_LIST_HEIGHT}, calc(var(--radix-popover-content-available-height, ${maxHeight}) - ${availableHeightOffset}))`
  const resolvedMaxHeight = `min(${maxHeight}, ${availableHeight})`

  return (
    <div className="relative flex min-h-0 flex-col rounded-input overflow-hidden" onPointerLeave={resetHighlight}>
      {/* The overlay lane is absolute against this wrapper, so the wrapper --
          not the scroll container -- carries the `mr-2` (6px). That puts the
          12px lane 6-18px in from the panel border: the rule lands at 18px and
          the thumb occupies 6-12px, the same positions the old native 18px
          scrollbar produced. `pr-6` (18px) keeps rows out of that lane and adds
          the 6px gap between a row's right edge and the thumb; it sits here
          rather than on the scroll container so a consumer's `className` can't
          override it. */}
      <div className={cn("relative flex min-h-0 flex-col", isScrollable ? "mr-2 pr-6" : "pr-2")}>
        <div
          ref={scrollContainerRef}
          onScroll={updateScrollState}
          className={cn(
            "pl-2 bg-off-white overflow-y-auto",
            HIDE_NATIVE_SCROLLBAR,
            className,
          )}
          style={{ maxHeight: resolvedMaxHeight }}
        >
          <CommandList className="max-h-none overflow-visible">
            {children}
          </CommandList>
          {isLoadingMore && (
            <div className="flex items-center justify-center py-3" role="status">
              <div className="h-4 w-4 rounded-full border-2 border-grey-light border-t-grey animate-spin" />
              <span className="sr-only">Loading…</span>
            </div>
          )}
        </div>
        <ScrollbarOverlay targetRef={scrollContainerRef} />
      </div>
      {showFadeGradient && canScrollDown && (
        <div className="absolute bottom-0 left-0 right-6 h-8 bg-gradient-to-t from-off-white to-transparent pointer-events-none" />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  SearchableDropdown.Footer                                                 */
/* -------------------------------------------------------------------------- */

function SearchableDropdownFooter({ children }: { children: ReactNode }) {
  return <>{children}</>
}

/* -------------------------------------------------------------------------- */
/*  Attach compound components                                                */
/* -------------------------------------------------------------------------- */

export const SearchableDropdown = Object.assign(SearchableDropdownRoot, {
  Trigger: SearchableDropdownTrigger,
  Tabs: SearchableDropdownTabs,
  List: SearchableDropdownList,
  Footer: SearchableDropdownFooter,
})
